import { useId, type ReactNode } from 'react';
import { Checkbox, FormControl, Text } from '~/molecules';
import { ReadonlyField } from '../ReadonlyField';
import { useTranslator } from '~/translations/TranslatorContext';
import { StyleSheet } from 'react-native-unistyles';

interface BoolFieldProps {
    editable?: boolean;
    initialValue?: { value?: boolean | number | string | null } | null;
    isProtected?: boolean;
    isReadonly?: boolean;
    name?: string;
    required?: boolean;
    title: string;
    tooltip?: string;
    onChange: (value: boolean, event?: unknown) => void;
    value: boolean | number | string | null | undefined;
    validationMessage?: ReactNode;
}

export const BoolField = (props: BoolFieldProps) => {
    const { editable, initialValue, isProtected, isReadonly, name, required, title, tooltip, onChange, value, validationMessage } = props;

    const trans = useTranslator();
    const errorId = useId();
    const disabled = !editable || isProtected;
    const errorColor = validationMessage && !disabled ? 'error' : undefined;

    const handleChange = (nextValue: boolean, event?: unknown) => {
        if (isReadonly) return;
        onChange(nextValue, event);
    };

    const persistedValue = value ?? initialValue?.value ?? false;
    const checked = persistedValue === true || persistedValue === 1 || persistedValue === '1';

    if (isReadonly) {
        return <ReadonlyField name={name} tooltip={tooltip} label={title} value={checked ? trans('yes') : trans('no')} />;
    }

    return (
        <FormControl disabled={disabled}>
            <FormControl.Label label={title} required={required} tooltip={tooltip} />
            <Checkbox
                accessibilityLabel={name ?? title}
                aria-checked={checked}
                aria-required={!!required}
                aria-invalid={!!validationMessage}
                aria-describedby={validationMessage ? errorId : undefined}
                color={errorColor}
                uncheckedColor={errorColor}
                disabled={disabled}
                onChange={handleChange}
                size="md"
                style={styles.checkbox}
                value={checked}
            />
            {validationMessage && <Text nativeID={errorId} role="alert" style={styles.validationError}>{validationMessage}</Text>}
        </FormControl>
    );
};

const styles = StyleSheet.create((theme) => ({
    validationError: { color: theme.colors.error, fontSize: 13 },
    checkbox: {
        marginVertical: theme.spacings['2'],
    },
}));
